<template>
    <div class="flex flex-col">
        <div class="text-2xl font-medium text-gray-900">
            {{ title }}
        </div>

        <gift-card-payment-form
            v-if="showGiftCardPaymentTotal"
            :checkout-total="checkoutTotal"
            :payment-source-id="giftCardPaymentSourceId"
            class="mt-2"
        />

        <store-credit-payment
            v-if="storeCreditPaymentSourceId !== null && customerData !== null"
            class="mt-6"
            :customer="customerData"
            :after-gift-card-total="afterGiftCardTotal"
            :payment-source-id="storeCreditPaymentSourceId"
        />

        <div
            v-if="showCreditCardInput"
            class="mt-6 max-w-[650px]"
        >
            <div v-if="addingPaymentMethod">
                <div
                    class="mb-4 flex flex-col gap-2 md:flex-row md:items-center md:justify-between"
                >
                    <div v-if="storedPaymentMethods.length > 0">
                        {{
                            t('checkoutSections.enterPaymentMethod', {
                                amount: paymentMethodBalanceToUse.formatCurrency()
                            })
                        }}
                    </div>

                    <prime-button
                        v-if="
                            addingPaymentMethod &&
                            storedPaymentMethods.length > 0
                        "
                        outlined
                        :label="t('checkoutSections.useExistingPaymentMethod')"
                        class="whitespace-nowrap"
                        :disabled="isProcessing"
                        @click="handleCancelAddPaymentMethod"
                    />
                </div>

                <new-payment-method
                    ref="newPaymentMethodRef"
                    :amount="paymentMethodBalanceToUse"
                    :payment-method-input="paymentMethodInput"
                    :payment-source="paymentSourceCreditCard"
                    :error="errorPayment"
                    @payment-method="handleNewPaymentMethod"
                />
            </div>

            <div v-if="!addingPaymentMethod">
                {{
                    t('checkoutSections.choosePaymentForBalance', {
                        amount: paymentMethodBalanceToUse.formatCurrency()
                    })
                }}
            </div>

            <div
                v-if="!addingPaymentMethod"
                class="mb-4 mt-1 grid grid-cols-1 gap-4 md:grid-cols-2"
            >
                <payment-method-option
                    v-if="selectedPaymentMethod !== null"
                    :payment-method="selectedPaymentMethod"
                    :selected-token="selectedToken"
                    :editing="displayAllPaymentMethods"
                    @select-card="
                        handleSelectPaymentMethod(selectedPaymentMethod)
                    "
                />

                <template v-if="displayAllPaymentMethods">
                    <payment-method-option
                        v-for="paymentMethod in paymentMethods"
                        :key="paymentMethod.key"
                        :payment-method="paymentMethod"
                        :selected-token="selectedToken"
                        editing
                        @select-card="handleSelectPaymentMethod(paymentMethod)"
                    />

                    <prime-button
                        outlined
                        class="flex justify-center border-dashed"
                        :disabled="isProcessing"
                        @click="handleAddPaymentMethod"
                    >
                        <span
                            class="flex items-center justify-center gap-1 font-bold"
                        >
                            <i class="pi pi-fw pi-plus" />
                            {{ t('checkoutSections.addPaymentMethod') }}
                        </span>
                    </prime-button>
                </template>

                <div
                    v-else
                    class="flex flex-1 flex-col items-start gap-4 md:items-end"
                >
                    <prime-button
                        outlined
                        :label="t('checkoutSections.changePaymentMethod')"
                        class="whitespace-nowrap"
                        :disabled="isProcessing"
                        @click="displayAllPaymentMethods = true"
                    />
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import type { FragmentType } from '~/resources/graphql'
import { getFragmentData, graphql } from '~/resources/graphql'
import { array, mixed, object, string } from 'yup'
import { Decimal } from '~/composables/support/decimal'
import { toTypedSchema } from '@vee-validate/yup'
import type { TypedSchema } from 'vee-validate'

type TranslationFunction = (key: string, ...args: unknown[]) => string

export const PaymentInformationCustomerFragment = graphql(/* GraphQL */ `
    fragment PaymentInformationCustomerFragment on Customer {
        storedPaymentMethods {
            id
            formattedBrand
            formattedMaskedAccount
            isCreditCard
            isCustomerDefault
            lastUsedDate
            accountExpirationDate
            migrationRequired
            paymentTodayAllowed
            paymentSource {
                id
            }
        }
        ...StoreCreditPaymentCustomerFragment
    }
`)

export const PaymentInformationFacilityFragment = graphql(/* GraphQL */ `
    fragment PaymentInformationFacilityFragment on Facility {
        ...StoredPaymentInformationFacilityFragment
        cpxPaymentSources {
            id
            data {
                __typename
            }
            ...NewPaymentMethodPaymentSourceFragment
        }
    }
`)

export function PaymentInformationSchema(t: TranslationFunction) {
    return object({
        // Put the total in the form so we can use it in validation
        total: string().required(),
        paymentMethod: object({
            paymentSourceId: string().required(),
            amount: string().required(),
            type: mixed()
                .oneOf([
                    'StoredPaymentMethod', // Stored already on the server
                    'PaymentMethod', // Providing new PUP token
                    'RecurringPaymentMethod' // Using the PUP token provided by recurring
                ])
                .required(),
            id: string().defined().nullable(),
            token: string().defined().nullable()
        })
            .nullable()
            .defined(),
        giftCardInputValue: string().max(
            0,
            t('checkoutSections.giftCardApplyOrRemove')
        ),
        giftCards: array()
            .of(
                object({
                    currentBalance: string().required(),
                    paymentSourceId: string().required(),
                    amount: string().required(),
                    token: string().required()
                })
            )
            .required(),
        storeCredit: object({
            paymentSourceId: string().required(),
            amountUsed: string().nullable().defined()
        })
            .nullable()
            .defined()
    }).test(
        'payment-method',
        t('checkoutSections.paymentMethodRequired'),
        value => {
            if (value.total === undefined) {
                return false
            }

            let totalAfterGiftCardsAndStoreCredit = new Decimal(value.total)
            value.giftCards.forEach(giftCard => {
                totalAfterGiftCardsAndStoreCredit =
                    totalAfterGiftCardsAndStoreCredit.minus(
                        new Decimal(giftCard.amount)
                    )
            })

            if (value.storeCredit !== null) {
                if (value.storeCredit.amountUsed === null) {
                    return false
                }

                totalAfterGiftCardsAndStoreCredit =
                    totalAfterGiftCardsAndStoreCredit.minus(
                        new Decimal(value.storeCredit.amountUsed)
                    )
            }

            const giftCardsAndStoreCreditNotSufficient =
                totalAfterGiftCardsAndStoreCredit.isPositiveAndNotZero()

            let noCreditCard = true
            if (value.paymentMethod !== null) {
                if (value.paymentMethod.type === 'RecurringPaymentMethod') {
                    noCreditCard = false
                }

                if (
                    value.paymentMethod.type === 'StoredPaymentMethod' &&
                    typeof value.paymentMethod.id === 'string'
                ) {
                    noCreditCard = false
                }

                if (
                    value.paymentMethod.type === 'PaymentMethod' &&
                    typeof value.paymentMethod.token === 'string'
                ) {
                    noCreditCard = false
                }
            }

            // If gift cards are not sufficient, check for no card
            if (giftCardsAndStoreCreditNotSufficient && noCreditCard) {
                return false
            }

            return true
        }
    )
}

const translationStub: TranslationFunction = () => ''
const paymentInformationSchema = toTypedSchema(
    PaymentInformationSchema(translationStub)
)
export type PaymentInformationSchemaType =
    typeof paymentInformationSchema extends TypedSchema<unknown, infer TOutput>
        ? TOutput
        : never
export type GiftCardPaymentInfo = PaymentInformationSchemaType['giftCards'][0]
export type StoreCreditPaymentInfo = PaymentInformationSchemaType['storeCredit']

export function PaymentInformationInitialValues(total: Decimal) {
    return {
        total: total.toFixed(2),
        paymentMethod: null,
        giftCardInputValue: '',
        giftCards: [],
        storeCredit: null
    }
}
</script>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { useField } from 'vee-validate'
import GiftCardPaymentForm from '~/components/Checkout/Sections/Payment/GiftCardPaymentForm.vue'
import PrimeButton from 'primevue/button'
import PaymentMethodOption from '~/components/Checkout/Sections/Payment/PaymentMethodOption.vue'
import NewPaymentMethod from '~/components/Checkout/Sections/Payment/NewPaymentMethod.vue'
import type { PaymentMethodProperties } from '~/components/Checkout/Sections/Payment/payment-method'
import StoreCreditPayment from '~/components/Checkout/Sections/Payment/StoreCreditPayment.vue'
import type { RecurringInformationSchemaType } from '~/components/Checkout/Sections/RecurringInformation.vue'
import { useCheckoutFormProcessing } from '~/components/Checkout/Forms/use-checkout-form'
import { type PaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import { useI18n } from 'vue-i18n'

const props = defineProps<{
    customer: FragmentType<typeof PaymentInformationCustomerFragment> | null
    facility: FragmentType<typeof PaymentInformationFacilityFragment>
    checkoutTotal: Decimal
    title: string
    paymentMethodInput: PaymentMethodInput
}>()

const { t } = useI18n({ useScope: 'global' })

const customerData = computed(() =>
    props.customer === null
        ? null
        : getFragmentData(PaymentInformationCustomerFragment, props.customer)
)

const facilityData = computed(() =>
    getFragmentData(PaymentInformationFacilityFragment, props.facility)
)

const giftCardPaymentSourceId = computed(() => {
    const paymentSourceGiftCard = facilityData.value.cpxPaymentSources.find(
        source => source.data?.__typename === 'PaymentSourceGiftCard'
    )

    if (paymentSourceGiftCard === undefined) {
        return null
    }

    return paymentSourceGiftCard.id
})

const storeCreditPaymentSourceId = computed(() => {
    return (
        facilityData.value.cpxPaymentSources.find(
            source => source.data?.__typename === 'PaymentSourceStoreCredit'
        )?.id ?? null
    )
})

const { value: payment, errorMessage: errorPayment } =
    useField<PaymentInformationSchemaType>('payment', '', {
        validateOnValueUpdate: false
    })

const { value: recurring } = useField<RecurringInformationSchemaType>(
    'recurring',
    '',
    {
        validateOnValueUpdate: false,
        // Don't reset the values
        // see: https://github.com/logaretm/vee-validate/issues/4830
        keepValueOnUnmount: true,
        initialValue: undefined
    }
)

// This weird thing happens when you edit the recurring form, the payment form gets reset
// I couldn't figure out where that was coming from, so I just set the initial values again
if (payment.value === undefined) {
    payment.value = PaymentInformationInitialValues(props.checkoutTotal)
}

const giftCardPaymentTotal = computed(() => {
    return payment.value.giftCards.reduce(
        (accum, giftCard) => accum.plus(new Decimal(giftCard.amount)),
        Decimal.zero()
    )
})

const showGiftCardPaymentTotal = computed(() => {
    return (
        props.checkoutTotal.isPositiveAndNotZero() &&
        giftCardPaymentSourceId.value !== null
    )
})

const afterGiftCardTotal = computed(() => {
    return props.checkoutTotal.minus(giftCardPaymentTotal.value)
})

const storeCreditPaymentTotal = computed(() => {
    if (payment.value.storeCredit !== null) {
        return new Decimal(payment.value.storeCredit.amountUsed ?? '0')
    }

    return Decimal.zero()
})

const paymentMethodBalanceToUse = computed(() => {
    return afterGiftCardTotal.value.minus(storeCreditPaymentTotal.value)
})

const showCreditCardInput = computed(() =>
    paymentMethodBalanceToUse.value.isPositiveAndNotZero()
)

const recurringPaymentMethod = computed<PaymentMethodProperties>(() => {
    if (
        recurring.value.paymentMethod !== null &&
        recurring.value.paymentMethod.paymentTodayAllowed
    ) {
        return recurring.value.paymentMethod.object
    }

    return null
})

const storedPaymentMethods = computed<PaymentMethodProperties[]>(() => {
    const paymentMethods: PaymentMethodProperties[] = []
    if (recurringPaymentMethod.value !== null) {
        paymentMethods.push({
            ...recurringPaymentMethod.value
        })
    }

    if (customerData.value !== null) {
        paymentMethods.push(
            ...customerData.value.storedPaymentMethods
                .filter(paymentMethod => {
                    // Migration required payment methods not allowed
                    if (paymentMethod.migrationRequired) {
                        return false
                    }

                    // Must be payment today allowed
                    if (!paymentMethod.paymentTodayAllowed) {
                        return false
                    }

                    if (recurringPaymentMethod.value !== null) {
                        // We've already selected this payment method in the recurring section
                        // It will be added above so do not add it here
                        if (
                            recurringPaymentMethod.value.key ===
                            paymentMethod.id
                        ) {
                            return false
                        }
                    }

                    // This method can be selected
                    return true
                })
                .map(paymentMethod => {
                    const mapped: PaymentMethodProperties = {
                        key: paymentMethod.id,
                        paymentSourceId: paymentMethod.paymentSource.id,
                        displayInfo: {
                            isCard: paymentMethod.isCreditCard,
                            brand: paymentMethod.formattedBrand,
                            lastFour: paymentMethod.formattedMaskedAccount,
                            accountExpirationDate:
                                paymentMethod.accountExpirationDate,
                            lastUsedDate: paymentMethod.lastUsedDate
                        },
                        paymentTodayAllowed: paymentMethod.paymentTodayAllowed,
                        data: {
                            type: 'StoredPaymentMethod',
                            id: paymentMethod.id,
                            isCustomerDefault: paymentMethod.isCustomerDefault,
                            migrationRequired: paymentMethod.migrationRequired
                        }
                    }

                    return mapped
                })
        )
    }

    return paymentMethods
})

const displayAllPaymentMethods = ref(false)
const addedPaymentMethod = ref<PaymentMethodProperties | null>(null)
const paymentMethods = computed(() => {
    return storedPaymentMethods.value.filter(paymentMethod => {
        return selectedToken.value !== paymentMethod.key
    })
})

const selectedToken = computed(() => {
    if (payment.value.paymentMethod === null) {
        return ''
    }

    if (payment.value.paymentMethod.type === 'RecurringPaymentMethod') {
        return recurringPaymentMethod.value.key
    }

    if (payment.value.paymentMethod.type === 'StoredPaymentMethod') {
        return payment.value.paymentMethod.id
    }

    return payment.value.paymentMethod.token
})

const selectedPaymentMethod = computed<PaymentMethodProperties | null>(() => {
    if (selectedToken.value === '') {
        return null
    }

    if (
        addedPaymentMethod.value !== null &&
        addedPaymentMethod.value.key === selectedToken.value
    ) {
        return addedPaymentMethod.value
    }

    return (
        storedPaymentMethods.value.find(paymentMethod => {
            return paymentMethod.key === selectedToken.value
        }) ?? null
    )
})

const addingPaymentMethod = ref(false)
function handleAddPaymentMethod(): void {
    // Wipe the current selection
    handleSelectPaymentMethod(null)
    addingPaymentMethod.value = true
}

function handleCancelAddPaymentMethod(): void {
    // Wipe the current selection
    handleSelectPaymentMethod(storedPaymentMethods.value[0])
    addingPaymentMethod.value = false
    displayAllPaymentMethods.value = false
}

const paymentSourceCreditCard = computed(() => {
    const paymentSourceCreditCard = facilityData.value.cpxPaymentSources.find(
        source =>
            source.data?.__typename === 'PaymentSourceCreditCardCardpointe' ||
            source.data?.__typename === 'PaymentSourceCreditCardStripe' ||
            source.data?.__typename === 'PaymentSourceCreditCardWindcave' ||
            source.data?.__typename === 'PaymentSourceCustom'
    )

    if (paymentSourceCreditCard === undefined) {
        return null
    }

    return paymentSourceCreditCard
})

// Update our form data when the checkout total changes
watch(
    () => props.checkoutTotal,
    newTotal => {
        // Our new total as a string
        payment.value = {
            ...payment.value,
            total: newTotal.toFixed(2)
        }
    }
)

watch(paymentMethodBalanceToUse, newValue => {
    // If we had an existing credit card payment
    if (payment.value.paymentMethod !== null) {
        // Did the total amount change
        const totalChanged = new Decimal(
            payment.value.paymentMethod.amount
        ).isNotEqualTo(newValue)

        if (totalChanged) {
            // Update credit card total
            payment.value = {
                ...payment.value,
                paymentMethod: {
                    ...payment.value.paymentMethod,
                    amount: newValue.toFixed(2)
                }
            }

            // TODO:
            // if (isStripe.value) {
            //     // If stripe, we need to make a new payment intent, so refresh the form
            //     if (
            //         newPaymentMethodRef.value !== undefined &&
            //         newPaymentMethodRef.value !== null
            //     ) {
            //         newPaymentMethodRef.value.refreshStripeForm()
            //     }
            //     // We've already confirmed the payment intent so now we need to reset
            //     else {
            //         payment.value = {
            //             ...payment.value,
            //             paymentMethod: null
            //         }
            //     }
            // }
        }
    }
    // No existing payment, wipe the form to get a new payment intent
    else {
        // TODO:
        // if (isStripe.value) {
        //     // If stripe, we need to make a new payment intent, so refresh the form
        //     if (
        //         newPaymentMethodRef.value !== undefined &&
        //         newPaymentMethodRef.value !== null
        //     ) {
        //         newPaymentMethodRef.value.refreshStripeForm()
        //     }
        // }
    }
})

function handleNewPaymentMethod(
    paymentMethod: PaymentMethodProperties | null
): void {
    if (paymentMethod === null) {
        addedPaymentMethod.value = null
    } else {
        addedPaymentMethod.value = paymentMethod
    }
}

function handleSelectPaymentMethod(
    paymentMethod: PaymentMethodProperties | null
) {
    // If we select an existing payment method when adding a new payment method
    // remove the payment method form
    if (paymentMethod !== null && addingPaymentMethod.value) {
        addingPaymentMethod.value = false
    }
    if (paymentMethod !== null && displayAllPaymentMethods.value) {
        displayAllPaymentMethods.value = false
    }

    let newSelectedPaymentMethod = null
    if (paymentMethod !== null) {
        if (paymentMethod.data.type === 'CardpointeDirectDebit') {
            throw new Error('Direct debit cannot be used as payment method')
        }

        if (
            recurringPaymentMethod.value !== null &&
            recurringPaymentMethod.value.key === paymentMethod.key
        ) {
            // If using an existing stored payment method, just select that
            if (paymentMethod.data.type === 'StoredPaymentMethod') {
                newSelectedPaymentMethod = {
                    paymentSourceId: paymentMethod.paymentSourceId,
                    amount: paymentMethodBalanceToUse.value.toFixed(2),
                    type: 'StoredPaymentMethod',
                    id: paymentMethod.data.id,
                    token: null
                }
            }
            // Otherwise, select the "use whatever we used for recurring" method
            else {
                newSelectedPaymentMethod = {
                    paymentSourceId: paymentMethod.paymentSourceId,
                    amount: paymentMethodBalanceToUse.value.toFixed(2),
                    type: 'RecurringPaymentMethod',
                    id: null,
                    token: null
                }
            }
        } else if (paymentMethod.data.type === 'StoredPaymentMethod') {
            newSelectedPaymentMethod = {
                paymentSourceId: paymentMethod.paymentSourceId,
                amount: paymentMethodBalanceToUse.value.toFixed(2),
                type: 'StoredPaymentMethod',
                id: paymentMethod.data.id,
                token: null
            }
        } else {
            newSelectedPaymentMethod = {
                paymentSourceId: paymentMethod.paymentSourceId,
                amount: paymentMethodBalanceToUse.value.toFixed(2),
                type: 'PaymentMethod',
                id: null,
                token: paymentMethod.data.token
            }
        }
    }

    payment.value = {
        ...payment.value,
        paymentMethod: newSelectedPaymentMethod
    }
}

const { isProcessing } = useCheckoutFormProcessing()

const newPaymentMethodRef = ref<InstanceType<typeof NewPaymentMethod>>()
async function submitPaymentForm() {
    if (
        newPaymentMethodRef.value !== undefined &&
        newPaymentMethodRef.value !== null
    ) {
        await newPaymentMethodRef.value.submitPaymentForm()
    }

    if (addedPaymentMethod.value !== null) {
        addingPaymentMethod.value = false
        handleSelectPaymentMethod(addedPaymentMethod.value)
    }
}

function shouldPreflightBeforeSubmittingPaymentForm(): boolean {
    // Windcave can authorize payment in its browser widget before commitCheckout
    // reaches backend cart validation, so keep the validation-only checkout
    // before collecting a Windcave payment session.
    if (
        newPaymentMethodRef.value === undefined ||
        newPaymentMethodRef.value === null
    ) {
        return false
    }

    return (
        paymentSourceCreditCard.value?.data?.__typename ===
        'PaymentSourceCreditCardWindcave'
    )
}

defineExpose({
    submitPaymentForm,
    shouldPreflightBeforeSubmittingPaymentForm
})

// When we load for first time, select the first payment method only if payment is due
if (paymentMethodBalanceToUse.value.isPositiveAndNotZero()) {
    if (storedPaymentMethods.value.length > 0) {
        handleSelectPaymentMethod(storedPaymentMethods.value[0])
    }
    // Otherwise, we need to add a new payment method
    else {
        addingPaymentMethod.value = true
    }
}
</script>
